import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import { debounce } from "../../../lib/debounce";
import type { Msg } from "../../../types/chat";
import { loadSnapshot, hydrateSnapshotMessages, persistSnapshot } from "../channelSnapshots";
import type { Channel } from "../chatTypes";

const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

/**
 * Saves a lightweight snapshot of the active channel (channel list + its messages) and
 * restores it on cold start so mobile renders instantly. Falls back to the service worker's
 * warmup snapshot when localStorage has nothing.
 */
export function useSnapshotPersistence({
  currentVoidId,
  currentChannelId,
  channels,
  msgsByKey,
  setChannels,
  setChanOrder,
  setCurrentChannelId,
  setMsgsByKey,
}: {
  currentVoidId: string;
  currentChannelId: string;
  channels: Channel[];
  msgsByKey: Record<string, Msg[]>;
  setChannels: Dispatch<SetStateAction<Channel[]>>;
  setChanOrder: Dispatch<SetStateAction<Record<string, string[]>>>;
  setCurrentChannelId: Dispatch<SetStateAction<string>>;
  setMsgsByKey: Dispatch<SetStateAction<Record<string, Msg[]>>>;
}) {
  const hydratedSnapshotRef = useRef<boolean>(false);
  const persistSnapshotDebounced = useMemo(() => debounce((spaceId: string, channelList: any[], chanId: string, msgs: Record<string, Msg[]>) => {
    persistSnapshot(spaceId, channelList, chanId, msgs);
  }, 400), []);

  // Hydrate from the last saved snapshot so mobile cold starts render instantly
  useEffect(() => {
    if (hydratedSnapshotRef.current) return;
    const spaceId = currentVoidId || 'home';
    const snap = loadSnapshot(spaceId);
    if (!snap) return;
    hydratedSnapshotRef.current = true;
    if (Array.isArray(snap.channelMeta) && snap.channelMeta.length) {
      setChannels((prev) => (prev.length ? prev : snap.channelMeta.map((c) => ({ id: c.id, name: c.name, type: c.type || 'text', locked: false, folderId: null, position: 0 } as any))));
      setChanOrder((prev) => ({ ...prev, [spaceId]: snap.channelMeta.map((c) => c.id) }));
    }
    if (snap.currentChannelId) {
      setCurrentChannelId((prev) => prev || snap.currentChannelId!);
    }
    if (snap.messages) {
      const hydrated = hydrateSnapshotMessages(snap.messages);
      setMsgsByKey((prev) => ({ ...prev, ...hydrated }));
    }
  }, [currentVoidId, setChanOrder]);

  // Fallback: ask SW for warmup snapshot if localStorage is empty/stale
  useEffect(() => {
    if (hydratedSnapshotRef.current) return;
    (async () => {
      try {
        const res = await fetch('/__warmup_snapshot__', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!data || typeof data.ts !== 'number') return;
        hydratedSnapshotRef.current = true;
        if (Array.isArray(data.messages) && data.channelId) {
          setCurrentChannelId((prev) => prev || (data.channelId.includes(':') ? data.channelId.split(':').slice(-1)[0] : data.channelId));
          setMsgsByKey((prev) => ({ ...prev, [data.channelId]: hydrateSnapshotMessages({ [data.channelId]: data.messages })[data.channelId] || [] }));
        }
      } catch { /* noop */ }
    })();
  }, []);

  // Persist lightweight snapshot when active channel or messages change
  useEffect(() => {
    if (!currentVoidId) return;
    const activeKey = k(currentVoidId, currentChannelId);
    const slice: Record<string, Msg[]> = {};
    if (msgsByKey[activeKey]?.length) slice[activeKey] = msgsByKey[activeKey];
    persistSnapshotDebounced(currentVoidId, channels, currentChannelId, slice);
  }, [channels, currentVoidId, currentChannelId, msgsByKey, persistSnapshotDebounced]);
}
